import { useEffect, useMemo, useRef, useState } from 'react';

/** A selectable option for {@link MultiSelectSearch}. */
export interface MultiSelectOption {
  id: string;
  name: string;
}

/**
 * A searchable multi-select with removable chips and a dropdown of remaining matches.
 * Selection state is controlled by the parent via `selected` + `onToggle`.
 */
export function MultiSelectSearch({
  label,
  options,
  selected,
  emptyText,
  placeholder,
  onToggle,
}: {
  label: string;
  options: ReadonlyArray<MultiSelectOption>;
  selected: string[];
  emptyText: string;
  placeholder: string;
  onToggle: (id: string, checked: boolean) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const byId = useMemo(() => new Map(options.map((o) => [o.id, o.name])), [options]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) => !selected.includes(o.id) && (q === '' || o.name.toLowerCase().includes(q)),
    );
  }, [options, selected, query]);

  return (
    <div ref={containerRef}>
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1 rounded-lg border border-slate-200 p-2">
        {selected.length > 0 ? (
          <div className="mb-2 flex flex-wrap gap-1">
            {selected.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700"
              >
                {byId.get(id) ?? 'Unknown'}
                <button
                  type="button"
                  className="text-brand-500 hover:text-brand-800"
                  aria-label={`Remove ${byId.get(id) ?? 'item'}`}
                  onClick={() => onToggle(id, false)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}

        {options.length === 0 ? (
          <p className="px-1 py-0.5 text-xs text-slate-400">{emptyText}</p>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={query}
              placeholder={placeholder}
              className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
            />
            {open ? (
              <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                {matches.length === 0 ? (
                  <li className="px-3 py-1.5 text-xs text-slate-400">No matches.</li>
                ) : (
                  matches.map((option) => (
                    <li key={option.id}>
                      <button
                        type="button"
                        className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                        onClick={() => {
                          onToggle(option.id, true);
                          setQuery('');
                        }}
                      >
                        {option.name}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
